import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { Subscribable } from 'src/shared/components/base-component/subscribable';
import { SidebarItem } from 'src/shared/components/sidebar/sidebar-item.interface';
import { SidebarLogo } from 'src/shared/components/sidebar/sidebar-logo.interface';
import { CommonConstant } from 'src/shared/constants/common.constants';
import { SidebarItem as SidebarItemEnum } from 'src/shared/enums/app/sidebar-item.enum';
import { IUserMenuItem } from '../../shared/components/header/components/user-menu/user-menu.component';
import { FavieWebAdminHeaderInput } from '../../shared/components/header/interfaces/favie-web-admin-header-input.interface';
import { HeaderService } from '../../shared/components/header/services/header.service';
import { SidebarService } from '../../shared/components/sidebar/sidebar.service';
import { IUser } from '../../shared/interfaces/user.interface';
import { FavieAuthService } from '../../shared/services/auth/favie-auth.service';
import { UserRoleCheckerService } from '../../shared/services/auth/user-role-checker.service';
import { ProfileService } from '../../shared/services/user/profile.service';
import { UserService } from '../modules/user/services/user.service';

@Injectable({ providedIn: 'root' })
export class AppService extends Subscribable {
  private mobileModeSubject$ = new Subject();

  constructor(
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly sidebarService: SidebarService,
    private readonly headerService: HeaderService,
    private readonly profileService: ProfileService,
    private readonly favieAuthService: FavieAuthService,
    private readonly userRoleCheckerService: UserRoleCheckerService,
    private readonly userService: UserService
  ) {
    super();
  }

  public setListParams(type: string, data: object) {
    this.subscribe(
      this.userService.setUserPreference({ type, data }),
      (rs) => { }
    );
  }

  public setToolsVisibility(visibility = false, sidebarItem?: SidebarItemEnum) {
    this.headerService.setVisibility(visibility);
    this.sidebarService.setSidebarVisibility(visibility);
    if (sidebarItem) {
      this.sidebarService.setActiveItem(sidebarItem);
    }
  }

  public getLogoApp(isCollapsed: boolean) {
    const logo = {
      image: {
        src: '',
        alt: '',
        width: '',
        height: '',
      },
    };
    if (isCollapsed) {
      return null;
    } else {
      logo.image.src = CommonConstant.APP_LOGO;
      logo.image.width = '40px';
      logo[`routerLink`] = ['/'];
    }
    return logo;
  }

  public isMobileMode(): Observable<any> {
    return this.mobileModeSubject$.asObservable();
  }

  public setMobileMode(isMobileMode: boolean): void {
    this.mobileModeSubject$.next(isMobileMode);
  }

  public getAppSidebarLogo(): SidebarLogo {
    const logo = {
      src: CommonConstant.APP_LOGO_MEDIUM,
      srcSmall: CommonConstant.APP_LOGO_SMALL,
      routerLink: '/dashboard',
    };
    return logo;
  }

  public getAppSidebarItems(): SidebarItem[] {
    const superAdminRouting = [];
    const adminRouting = [];
    const readonlyRouting = [];

    if (this.userRoleCheckerService.isSystemAdmin()) {
      superAdminRouting.push(this.getSubscriptionSidebarItem());
      superAdminRouting.push(this.getSettingSidebarItems());
      superAdminRouting.push(this.getWebappSidebarItems());
    }

    if (this.userRoleCheckerService.isAdmin()) {
      superAdminRouting.push(this.getSubscriptionSidebarItem());
      superAdminRouting.push(this.getWebappSidebarItems());
    }

    if (this.userRoleCheckerService.isReadOnly()) {
      readonlyRouting.push(this.getSubscriptionSidebarItem());
    }

    const items: SidebarItem[] = [
      this.getDashboardSidebarItems(),
      ...superAdminRouting,
      ...adminRouting,
      ...readonlyRouting,
    ];

    return items;
  }

  public getAppHeaderInput(
    isSignedIn: boolean,
    headerInput: FavieWebAdminHeaderInput
  ) {
    if (!isSignedIn || !headerInput) {
      return;
    }

    headerInput.userMenu = { menuItems: this.getAppHeaderMenuItems() };
    headerInput.backLabel = this.translateService.instant('back');
    const subscription = this.profileService
      .getProfile()
      .subscribe((user: IUser) => {
        const userFullName = [user?.firstName, user?.lastName]
          .filter((v) => v)
          .join(' ');
        headerInput.userMenu.displayName = userFullName;
        headerInput.userMenu.displayRole =
          this.favieAuthService.getUserRoles()[0];
      });
    this.addSubscription(subscription);
  }

  public getAppHeaderMenuItems(): IUserMenuItem[] {
    const items: IUserMenuItem[] = [
      {
        label: this.translateService.instant('Header.Menu.SignOut'),
        img: 'assets/icons/icon-signout.svg',
        action: () => {
          this.favieAuthService.logout();
          this.router.navigateByUrl('/auth/sign-in');
        },
      },
    ];
    return items;
  }

  private getDashboardSidebarItems() {
    return {
      label: this.translateService.instant('Sidebar.Item.Dashboard'),
      routerLink: '/dashboard',
      item: SidebarItemEnum.DASHBOARD,
      icon: 'icon-home',
    };
  }

  private getSettingSidebarItems() {
    return {
      label: this.translateService.instant('Sidebar.Item.Configuration'),
      isHadSub: true,
      hoverToShow: true,
      matIcon: 'settings',
      routerLink: '/parking-lot',
      item: SidebarItemEnum.SETTING,
      sub: [
        {
          label: this.translateService.instant('Sidebar.Item.ParkingLot'),
          routerLink: '/parking-lot',
          item: SidebarItemEnum.PARKING_LOT,
        },
        {
          label: this.translateService.instant('Sidebar.Item.ApartmentComplex'),
          routerLink: '/apartment-complex',
          item: SidebarItemEnum.APARTMENT_COMPLEX,
        },
        {
          label: this.translateService.instant('Sidebar.Item.ParkingZone'),
          routerLink: '/parking-zone',
          item: SidebarItemEnum.PARKING_ZONE,
        },
        {
          label: this.translateService.instant('Sidebar.Item.Street'),
          routerLink: '/street',
          item: SidebarItemEnum.STREET,
        },
        {
          label: this.translateService.instant('Sidebar.Item.Job'),
          routerLink: '/job',
          item: SidebarItemEnum.JOB,
        },
        {
          label: this.translateService.instant(
            'Sidebar.Item.SupportiveDocument'
          ),
          routerLink: '/supportive-document',
          item: SidebarItemEnum.SUPPORTIVE_DOCUMENT,
        },
        {
          label: this.translateService.instant('Sidebar.Item.Setting'),
          routerLink: '/setting',
          item: SidebarItemEnum.SETTING,
        },
        {
          label: this.translateService.instant('Sidebar.Item.SubscriptionRenewalEmailTest'),
          routerLink: '/subscription-renewal-email-test',
          item: SidebarItemEnum.SUBSCRIPTION_RENEWAL_EMAIL_TEST,
        },
        {
          label: this.translateService.instant('Sidebar.Item.Operator'),
          routerLink: '/operator',
          item: SidebarItemEnum.OPERATOR,
        },
        {
          label: this.translateService.instant('Sidebar.Item.Subscription'),
          routerLink: '/subscription',
          item: SidebarItemEnum.SUBSCRIPTION,
        },
        {
          label: this.translateService.instant(
            'Sidebar.Item.SubscriptionCampaign'
          ),
          routerLink: '/subscription-campaign',
          item: SidebarItemEnum.SUBSCRIPTION_CAMPAIGN,
        },
      ],
    };
  }

  private getSubscriptionSidebarItem() {
    return {
      label: this.translateService.instant('Sidebar.Item.Subscription'),
      isHadSub: true,
      hoverToShow: true,
      matIcon: 'people',
      routerLink: '/user',
      item: SidebarItemEnum.CONFIGURATION,
      sub: [
        {
          label: this.translateService.instant('Sidebar.Item.User'),
          routerLink: '/user',
          item: SidebarItemEnum.USER,
        },
        {
          label: this.translateService.instant(
            'Sidebar.Item.UserSubscriptionRequest'
          ),
          routerLink: '/user-subscription-request',
          item: SidebarItemEnum.USER_SUBSCRIPTION_REQUEST,
        },
        {
          label: this.translateService.instant('Sidebar.Item.UserSubscription'),
          routerLink: '/user-subscription',
          item: SidebarItemEnum.USER_SUBSCRIPTION,
        },
        {
          label: this.translateService.instant('Sidebar.Item.Badge'),
          routerLink: '/badge',
          item: SidebarItemEnum.BADGE,
        },
        {
          label: this.translateService.instant('Sidebar.Item.Payment'),
          routerLink: '/request-payment',
          item: SidebarItemEnum.REQUEST_PAYMENT,
        },
      ],
    };
  }

  private getWebappSidebarItems() {
    return {
      label: this.translateService.instant('Sidebar.Item.Webapp'),
      isHadSub: true,
      hoverToShow: true,
      matIcon: 'category',
      routerLink: '/blog-article',
      item: SidebarItemEnum.WEBAPP,
      sub: [
        {
          label: this.translateService.instant('Sidebar.Item.BlogArticle'),
          routerLink: '/blog-article',
          item: SidebarItemEnum.BLOG_ARTICLE,
        },
        {
          label: this.translateService.instant('Sidebar.Item.BlogCategory'),
          routerLink: '/blog-category',
          item: SidebarItemEnum.BLOG_CATEGORY,
        },
        {
          label: this.translateService.instant('Sidebar.Item.Document'),
          routerLink: '/document',
          item: SidebarItemEnum.DOCUMENT,
        },
        {
          label: this.translateService.instant('Sidebar.Item.DocumentCategory'),
          routerLink: '/document-category',
          item: SidebarItemEnum.DOCUMENT_CATEGORY,
        },
      ],
    };
  }
}
